<template>
    <div class="h-100">
        <b-row align-h="center">
            <b-col cols="12" md="10">
                <b-link :to="{ name: 'settings-smartrouting' }">
                    <font-awesome-icon icon="arrow-left" />
                    {{ $t("Back") }}
                </b-link>
                <h1 class="mt-1">
                    {{ $t("settings.title") }} / {{ $t("settings.smart-routing.title") }} /
                    {{ $t("settings.smart-routing.edit.title") }}
                </h1>

                <hr />

                <div v-if="isLoading" class="d-flex justify-content-center mb-3">
                    <b-spinner />
                </div>

                <validation-observer v-if="!isLoading" ref="validationObserver" tag="form">
                    <app-basic-info />
                    <hr />
                    <app-channel />
                    <hr />
                    <app-contact-data />
                    <hr />
                    <app-templates />
                    <hr />
                    <app-keywords />
                    <hr />
                    <app-assignables />
                    <hr />
                    <contact-list />
                    <hr />
                    <app-business-hours />

                    <b-form-group content-cols-lg="7" content-cols-sm label-cols-lg="3" label-cols-sm="4">
                        <b-button
                            :disabled="isSaving"
                            class="d-flex justify-content-center mb-1"
                            variant="primary"
                            @click="editRoute"
                        >
                            <b-spinner v-if="isSaving" class="mr-50" small></b-spinner>
                            {{ $t("settings.smart-routing.edit.submit") }}
                        </b-button>
                    </b-form-group>
                </validation-observer>
            </b-col>
        </b-row>
    </div>
</template>
<script>
import { useRouter } from "@/@core/utils/utils";
import useChannels from "@/composables/v2.1/useChannels";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import i18n from "@/libs/i18n";
import { BButton, BCol, BContainer, BFormGroup, BLink, BRow, BSpinner } from "bootstrap-vue";
import { ValidationObserver } from "vee-validate";
import { useToast } from "vue-toastification/composition";
import AppAssignables from "./components/assignables";
import AppBasicInfo from "./components/basicInfo";
import AppChannel from "./components/channel";
import AppBusinessHours from "./components/businessHours";
import AppContactData from "./components/contactData";
import AppKeywords from "./components/keywords";
import AppTemplates from "./components/templates";
import useRoutes from "./useRoutes";
import ContactList from "@/views/modules/settings/smart-routing/components/contactList.vue";

export default {
    components: {
        ContactList,
        BContainer,
        BRow,
        BCol,
        BLink,
        BButton,
        BFormGroup,
        BSpinner,
        AppAssignables,
        AppBasicInfo,
        AppBusinessHours,
        AppChannel,
        AppContactData,
        AppTemplates,
        AppKeywords,
        ValidationObserver,
    },
    setup() {
        const { router } = useRouter();
        const toast = useToast();
        const { parseRouteForSave, currentRoute, fetchCurrentRoute, isLoading, isSaving, saveCurrentRoute } =
            useRoutes();
        const { fetchChannels } = useChannels();

        fetchCurrentRoute(Number(router.currentRoute.params.id)).catch(() => {
            toast({
                component: ToastificationContent,
                props: {
                    title: i18n.t("settings.smart-routing.edit.error-fetching"),
                    text: i18n.t("settings.smart-routing.edit.couldnt-get-route"),
                    icon: "times",
                    variant: "danger",
                },
            });
            router.replace({ name: "settings-smartrouting" });
        });
        fetchChannels({ params: { join: ["userChannels", "teamChannels"] } });

        return {
            currentRoute,
            toast,
            parseRouteForSave,
            saveCurrentRoute,
            isLoading,
            isSaving,
        };
    },
    methods: {
        editRoute() {
            return new Promise((resolve, reject) => {
                return this.$refs.validationObserver.validate().then((success) => {
                    if (success) {
                        try {
                            this.parseRouteForSave();
                        } catch (error) {
                            let translationKey = "settings.smart-routing.edit.error";
                            switch (error.message) {
                                case "Incomplete record":
                                    translationKey = "settings.smart-routing.contact-data.missing-input";
                                    break;
                                case "Invalid identity format":
                                    translationKey = "settings.smart-routing.identities.invalid-format";
                                    break;
                                case "Invalid template":
                                    translationKey = "settings.smart-routing.templates.errors.invalid-template";
                                    break;
                            }

                            this.toast.error(i18n.t(translationKey));
                            return reject();
                        }
                        return this.saveCurrentRoute()
                            .then(() => {
                                this.toast.success(i18n.t("settings.smart-routing.edit.success"));
                                this.$router.push({ name: "settings-smartrouting" });
                                resolve();
                            })
                            .catch(() => {
                                this.toast.error(i18n.t("settings.smart-routing.edit.error"));
                                reject();
                            });
                    } else {
                        this.toast.error(i18n.t("settings.smart-routing.edit.error"));
                        reject();
                    }
                });
            }).catch(() => {});
        },
    },
};
</script>
